import { Metadata, MetadataRoute } from "next";

import { getAbsoluteUrl, getSiteUrl, isPreviewUrl } from "@/core/host";
import { BCP47LangCode, fallbackLng, languages } from "@/core/i18n";

import { SITE_NAME } from "./seo.config";

/**
 * Converts a BCP47 language code to an OpenGraph locale.
 * e.g. en-US -> en_US (preferred by OpenGraph)
 */
export function toOpenGraphLocale(lang: BCP47LangCode): string {
  return lang.replace("-", "_");
}

type PageMetadataArgs = {
  lang: BCP47LangCode;
  title: string;
  description: string;
  imageSrc?: string;
  pathname?: string;
  canonicalLang?: BCP47LangCode;
};

export function getMetadataForPage({
  lang,
  title,
  description,
  imageSrc,
  pathname,
  canonicalLang,
}: PageMetadataArgs): Metadata {
  const siteUrl = getSiteUrl();
  const normalizedPathname = normalizePathname(pathname ?? `/${lang}`);
  const canonicalLangToUse = canonicalLang ?? lang;
  const canonicalPathname = replaceLeadingLang(normalizedPathname, lang, canonicalLangToUse);
  const canonicalUrl = getAbsoluteUrl(canonicalPathname, siteUrl);

  const languageAlternates: Record<string, string> = Object.fromEntries(
    languages.map((altLang) => {
      const altPathname = replaceLeadingLang(normalizedPathname, lang, altLang);

      return [altLang, getAbsoluteUrl(altPathname, siteUrl)];
    }),
  );
  languageAlternates["x-default"] = getAbsoluteUrl(
    replaceLeadingLang(normalizedPathname, lang, fallbackLng),
    siteUrl,
  );

  const alternateLocales = languages.filter((altLang) => altLang !== lang).map(toOpenGraphLocale);

  return {
    metadataBase: new URL(siteUrl),
    title,
    description,
    robots: { index: true, follow: true },
    alternates: {
      canonical: canonicalUrl,
      languages: languageAlternates,
    },
    openGraph: {
      type: "website",
      url: canonicalUrl,
      title,
      description,
      siteName: SITE_NAME,
      locale: toOpenGraphLocale(lang),
      alternateLocale: alternateLocales,
      ...(imageSrc
        ? {
            images: [{ url: imageSrc, alt: title }],
          }
        : {}),
    },
  };
}

export function resolveCanonicalLang({
  lang,
  defaultLang,
  localizedTitle,
  localizedDescription,
  defaultTitle,
  defaultDescription,
}: {
  lang: BCP47LangCode;
  defaultLang: BCP47LangCode;
  localizedTitle: string;
  localizedDescription: string;
  defaultTitle: string;
  defaultDescription: string;
}): BCP47LangCode {
  if (lang === defaultLang) return defaultLang;

  const isTranslated =
    localizedTitle !== defaultTitle || localizedDescription !== defaultDescription;

  return isTranslated ? lang : defaultLang;
}

type ContactPointJsonLd = {
  "@type": "ContactPoint";
  "contactType": string;
  "email": string;
};

type OrganizationJsonLd = {
  "@context": "https://schema.org";
  "@type": "Organization";
  "name": string;
  "url": string;
  "logo"?: string;
  "sameAs"?: string[];
  "contactPoint"?: ContactPointJsonLd;
};

/**
 * Global Organization JSON-LD.
 */
export function getOrganizationJsonLd(): OrganizationJsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Ageless AI",
    "url": getSiteUrl(),
    "logo": getAbsoluteUrl("/android-chrome-512x512.png", getSiteUrl()),
    "sameAs": ["https://ageless.ai"],
    "contactPoint": {
      "@type": "ContactPoint" as const,
      "contactType": "sales",
      "email": "info@ageless.ai",
    },
  };
}

type WebSiteJsonLd = {
  "@type": "WebSite";
  "name": string;
  "url": string;
  "inLanguage": string;
};

type WebPageJsonLd = {
  "@type": "WebPage";
  "name": string;
  "description": string;
  "url": string;
  "inLanguage": string;
  "isPartOf": Pick<WebSiteJsonLd, "@type" | "name" | "url">;
};

export type PageJsonLd = {
  "@context": "https://schema.org";
  "@graph": [WebSiteJsonLd, WebPageJsonLd];
};

export function getPageJsonLd({
  lang,
  description,
  name,
  pathname,
}: {
  lang: BCP47LangCode;
  description: string;
  /** The page's own title. Defaults to the site name, which is only right for the home page. */
  name?: string;
  /** The page's own path, locale prefix included. Defaults to the locale home. */
  pathname?: string;
}): PageJsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": SITE_NAME,
        "url": getSiteUrl(),
        "inLanguage": lang,
      },
      {
        "@type": "WebPage",
        "name": name ?? SITE_NAME,
        description,
        "url": getAbsoluteUrl(pathname ?? `/${lang}`, getSiteUrl()),
        "inLanguage": lang,
        "isPartOf": {
          "@type": "WebSite",
          "name": SITE_NAME,
          "url": getSiteUrl(),
        },
      },
    ],
  };
}

export const getRobotsConfiguration = () => {
  const siteUrl = getSiteUrl();

  const isProduction =
    process.env.VERCEL_ENV === "production" ||
    process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === "production";

  const shouldAllowIndexing = isProduction;

  const previewDisallows = ["/preview", "/preview/", "/*/preview", "/*/preview/"];

  const brandArtifactDisallows = ["/llms.txt", "/*/brand", "/*/brand-kit/prompt"];

  // Assessment funnel pages (/{lang}/a/{tenant}/{flow}/{step}) already carry
  // noindex meta, but a crawler must fetch and JS-render a page to see that —
  // and rendering a start step auto-creates a real backend assessment session.
  // Blocking crawl here stops the fetch entirely. Tenant funnels are reached
  // via direct/social links, so they need no search indexing.
  const assessmentFunnelDisallows = ["/a/", "/*/a/"];

  return {
    rules: shouldAllowIndexing
      ? [
          {
            userAgent: "*",
            allow: "/",
            disallow: [
              ...previewDisallows,
              ...brandArtifactDisallows,
              ...assessmentFunnelDisallows,
            ],
          },
        ]
      : { userAgent: "*", disallow: "/" },
    sitemap: shouldAllowIndexing ? `${siteUrl}/sitemap.xml` : undefined,
    host: siteUrl,
  };
};

export const getSitemapConfiguration = (): MetadataRoute.Sitemap => {
  const siteUrl = getSiteUrl();

  // Build time is the home page's real last-modified: it is statically generated from
  // source. This previously reported the publish date of a Storyblok `home` story that
  // no route rendered, so the sitemap advertised an unrelated draft's edit date.
  const now = new Date();

  const entries: MetadataRoute.Sitemap = [
    // Root redirects to the resolved locale; still useful as an entrypoint.
    {
      url: siteUrl,
      lastModified: now,
      changeFrequency: "yearly",
      priority: 0.2,
    },
  ];

  for (const lang of languages) {
    entries.push({
      url: `${siteUrl}/${lang}`,
      lastModified: now,
      changeFrequency: "weekly",
      priority: 1,
    });
    entries.push({
      url: `${siteUrl}/${lang}/signup`,
      lastModified: now,
      changeFrequency: "monthly",
      priority: 0.6,
    });
    entries.push({
      url: `${siteUrl}/${lang}/signup/start`,
      lastModified: now,
      changeFrequency: "monthly",
      priority: 0.6,
    });
    entries.push({
      url: `${siteUrl}/${lang}/find-a-practice`,
      lastModified: now,
      changeFrequency: "weekly",
      priority: 0.8,
    });
    entries.push({
      url: `${siteUrl}/${lang}/reviews`,
      lastModified: now,
      changeFrequency: "weekly",
      priority: 0.8,
    });
  }

  // Hard guard: never emit preview URLs, even if sitemap generation expands later.
  return entries.filter((e) => !isPreviewUrl(e.url));
};

function normalizePathname(pathname: string): string {
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function replaceLeadingLang(
  pathname: string,
  currentLang: BCP47LangCode,
  nextLang: BCP47LangCode,
): string {
  const pattern = new RegExp(`^/${currentLang}(?=/|$)`);
  return pathname.replace(pattern, `/${nextLang}`);
}
